import { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Phone, Mail, MessageCircle, ExternalLink, MapPin, Building2, Globe,
  History, CalendarClock, CheckCircle2, Clock, RotateCw, UserCog, X,
  IndianRupee, Plus, Banknote, Trophy, Pencil, XCircle, ArrowRight, Trash2, Copy,
  StickyNote, Star, Sparkles, Pin, PinOff, PencilLine,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { logDev } from "@/lib/ux";
import { logLeadOpened, logStageChangeOpened } from "@/lib/audit";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { QualityChip, StageChip, ActivityTypeChip, PriorityChip, ActivityStatusChip, SlaChip } from "@/components/Chips";
import { Loading, ErrorView } from "@/components/StateViews";
import ActivityModal from "@/components/ActivityModal";
import ChangeStageModal from "@/components/ChangeStageModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import NoteModal from "@/components/NoteModal";
import NoteHistoryModal from "@/components/NoteHistoryModal";
import QualEditRequestModal from "@/components/QualEditRequestModal";
import DealDetailsModal from "@/components/DealDetailsModal";
import { buildTimeline, groupByDay } from "@/lib/timeline";
import { fmtDateTime, fmtDate, initials, fmtINR, isOverdue } from "@/lib/format";

// Timeline event category -> { icon, tint } (premium, calm palette).
const TL_META = {
  stage: { icon: ArrowRight, tint: "text-indigo-600 bg-indigo-50" },
  activity: { icon: CalendarClock, tint: "text-cyan-600 bg-cyan-50" },
  note: { icon: StickyNote, tint: "text-amber-600 bg-amber-50" },
  assignment: { icon: UserCog, tint: "text-loop bg-loop/10" },
  qualification: { icon: Star, tint: "text-violet-600 bg-violet-50" },
  deal: { icon: Banknote, tint: "text-emerald-600 bg-emerald-50" },
  payment: { icon: Banknote, tint: "text-emerald-600 bg-emerald-50" },
  system: { icon: Sparkles, tint: "text-ink/60 bg-mist" },
};

const isEmptyChild = (c) =>
  c === null || c === undefined || c === false || c === "" ||
  (Array.isArray(c) && c.length === 0);

const Field = ({ label, children }) => {
  // Part 3: never render a label with a blank value — hide the whole row instead.
  if (isEmptyChild(children)) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/40">{label}</p>
      <div className="mt-0.5 text-sm text-ink">{children}</div>
    </div>
  );
};

const Section = ({ title, children, action }) => (
  <div className="rounded-2xl border border-sky/70 bg-white p-5">
    <div className="mb-4 flex items-center justify-between">
      <h4 className="font-sora text-sm font-semibold text-navy">{title}</h4>
      {action}
    </div>
    {children}
  </div>
);

function Select({ value, onChange, options, placeholder, testid }) {
  return (
    <select
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="h-10 w-full rounded-lg border border-sky bg-white px-2.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function LeadDrawer({ leadId, open, onOpenChange, onUpdated, users = [] }) {
  const { options, can, has, user } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [newPay, setNewPay] = useState({ amount: "", received_at: "", note: "" });
  const [activities, setActivities] = useState([]);
  const [qualHistory, setQualHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [qualSnapshot, setQualSnapshot] = useState(null);
  const [dealDetails, setDealDetails] = useState(null);
  const [dealModalOpen, setDealModalOpen] = useState(false);
  const [noteModal, setNoteModal] = useState(null);       // null | 'new' | noteObj
  const [noteHistoryNote, setNoteHistoryNote] = useState(null);
  const [delNote, setDelNote] = useState(null);
  const [qualEditOpen, setQualEditOpen] = useState(false);
  const [pinnedExpanded, setPinnedExpanded] = useState(false);
  const [timelineMode, setTimelineMode] = useState("key"); // 'key' | 'all'
  const [actModal, setActModal] = useState(null); // null | 'new' | activityObj
  const [changeStageOpen, setChangeStageOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ quality: "", reason: "" });
  const [delOpen, setDelOpen] = useState(false);
  const isAdmin = user?.role === "Admin";
  const isManager = user?.role === "Admin" || user?.role === "Sales Manager";

  const loadNotes = useCallback(async () => {
    if (!leadId) return;
    try {
      const { data } = await api.get(`/leads/${leadId}/notes`);
      setNotes(data.data);
    } catch (e) { logDev("Failed to load notes", e); }
  }, [leadId]);

  const loadQualSnapshot = useCallback(async () => {
    if (!leadId) return;
    try {
      const { data } = await api.get(`/leads/${leadId}/qualification-snapshot`);
      setQualSnapshot(data.data);
    } catch (e) { logDev("Failed to load qualification snapshot", e); }
  }, [leadId]);

  const loadDealDetails = useCallback(async () => {
    if (!leadId) return;
    try {
      const { data } = await api.get(`/leads/${leadId}/deal-details`);
      setDealDetails(data.data);
    } catch (e) { logDev("Failed to load deal details", e); }
  }, [leadId]);

  const loadActivities = useCallback(async () => {
    if (!leadId) return;
    try {
      const { data } = await api.get(`/leads/${leadId}/activities`);
      setActivities(data.data);
    } catch (e) { logDev("Failed to load lead activities", e); }
  }, [leadId]);

  const loadQualHistory = useCallback(async () => {
    if (!leadId) return;
    try {
      const { data } = await api.get(`/leads/${leadId}/qualification-history`);
      setQualHistory(data.data);
    } catch (e) { logDev("Failed to load qualification history", e); }
  }, [leadId]);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/leads/${leadId}`);
      setLead(data.data);
      setForm(data.data);
      loadActivities();
      loadQualHistory();
      loadNotes();
      loadQualSnapshot();
      loadDealDetails();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [leadId, loadActivities, loadQualHistory, loadNotes, loadQualSnapshot, loadDealDetails]);

  useEffect(() => { if (open && leadId) load(); }, [open, leadId, load]);
  useEffect(() => { if (open && leadId) logLeadOpened(leadId); }, [open, leadId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const actAction = async (id, type, days) => {
    try {
      if (type === "complete") await api.post(`/activities/${id}/complete`);
      else if (type === "cancel") await api.post(`/activities/${id}/cancel`);
      else await api.post(`/activities/${id}/snooze`, { days });
      toast.success(type === "complete" ? "Activity completed" : type === "cancel" ? "Activity cancelled" : `Snoozed ${days}d`);
      loadActivities(); load(); onUpdated?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        service_interested: form.service_interested,
        budget_range: form.budget_range, business_segment: form.business_segment,
        business_category: form.business_category, deal_value_estimate: form.deal_value_estimate,
        lost_reason: form.lost_reason,
        next_follow_up_at: form.next_follow_up_at, follow_up_note: form.follow_up_note,
        deal_services: form.deal_services, payment_structure: form.payment_structure,
      };
      const { data } = await api.put(`/leads/${leadId}`, payload);
      setLead(data.data); setForm(data.data);
      toast.success("Lead updated");
      onUpdated?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleService = (s) => {
    const cur = form.deal_services || [];
    set("deal_services", cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  };

  const addPayment = async () => {
    if (!newPay.amount || Number(newPay.amount) <= 0) return toast.error("Enter a valid amount");
    try {
      const { data } = await api.post(`/leads/${leadId}/payments`, {
        amount: Number(newPay.amount),
        received_at: newPay.received_at ? new Date(newPay.received_at).toISOString() : undefined,
        note: newPay.note || undefined,
      });
      setLead(data.data); setForm(data.data);
      setNewPay({ amount: "", received_at: "", note: "" });
      toast.success("Payment recorded");
      onUpdated?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const reassign = async (uid) => {
    try {
      await api.post(`/leads/${leadId}/reassign`, { owner_user_id: uid });
      toast.success("Lead reassigned");
      load(); onUpdated?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rerun = async () => {
    try {
      const { data } = await api.post(`/leads/${leadId}/rerun-assignment`);
      toast.success(data.data.is_unassigned ? "No eligible owner — left unassigned" : `Reassigned to ${data.data.owner_name}`);
      load(); onUpdated?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitOverride = async () => {
    if (!overrideForm.quality || !overrideForm.reason.trim()) return toast.error("Quality and reason are required");
    try {
      await api.post(`/leads/${leadId}/override-quality`, overrideForm);
      toast.success("Quality overridden");
      setOverrideOpen(false); setOverrideForm({ quality: "", reason: "" });
      load(); onUpdated?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead moved to recycle bin");
      onOpenChange(false);
      onUpdated?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const pinToggle = async (note) => {
    try {
      await api.post(`/leads/${leadId}/notes/${note.id}/pin`, { pinned: !note.is_pinned });
      loadNotes();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDeleteNote = async () => {
    if (!delNote) return;
    try {
      await api.delete(`/leads/${leadId}/notes/${delNote.id}`);
      toast.success("Note deleted");
      setDelNote(null);
      loadNotes();
    } catch (e) { toast.error(apiError(e)); }
  };

  const canEditNote = (note) => isManager || note.created_by_user_id === user?.id;
  const pinnedNotes = notes.filter((n) => n.is_pinned);
  const timelineEvents = buildTimeline(lead?.activity_history, qualHistory);
  const visibleEvents = timelineMode === "key" ? timelineEvents.filter((e) => e.key) : timelineEvents;
  const timelineGroups = groupByDay(visibleEvents);

  const NoteCard = ({ note, compact }) => (
    <div data-testid={`note-${note.id}`} className={`rounded-xl border p-3 ${note.is_pinned ? "border-amber-200 bg-amber-50/40" : "border-sky/70 bg-white"}`}>
      <p className="whitespace-pre-wrap text-sm text-ink">{note.content}</p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="text-[11px] text-ink/45">
          {note.created_by_name || "—"} · {fmtDateTime(note.created_at)}
          {note.updated_at && <span className="text-ink/40"> · edited {fmtDateTime(note.updated_at)}</span>}
        </p>
        {!compact && (
          <div className="flex shrink-0 items-center gap-1">
            <button onClick={() => pinToggle(note)} data-testid={`note-pin-${note.id}`} title={note.is_pinned ? "Unpin" : "Pin"} className={`rounded-lg p-1.5 ${note.is_pinned ? "text-amber-600 hover:bg-amber-100" : "text-ink/40 hover:bg-mist"}`}>
              {note.is_pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
            </button>
            {isManager && (note.edit_history?.length > 0) && (
              <button onClick={() => setNoteHistoryNote(note)} data-testid={`note-history-btn-${note.id}`} title="View history" className="rounded-lg p-1.5 text-ink/40 hover:bg-mist"><History className="h-3.5 w-3.5" /></button>
            )}
            {canEditNote(note) && (
              <button onClick={() => setNoteModal(note)} data-testid={`note-edit-${note.id}`} title="Edit" className="rounded-lg p-1.5 text-loop hover:bg-sky/30"><Pencil className="h-3.5 w-3.5" /></button>
            )}
            {canEditNote(note) && (
              <button onClick={() => setDelNote(note)} data-testid={`note-delete-${note.id}`} title="Delete" className="rounded-lg p-1.5 text-red-400 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const phoneDigits = (lead?.phone || "").replace(/[^\d+]/g, "");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="lead-drawer"
        className="w-full overflow-y-auto border-l border-sky bg-mist p-0 sm:max-w-xl"
      >
        <SheetTitle className="sr-only">Lead details</SheetTitle>
        {loading ? <Loading /> : error ? <ErrorView message={error} onRetry={load} /> : lead && (
          <div>
            {/* Header */}
            <div className="sticky top-0 z-10 border-b border-sky bg-white/90 px-5 py-4 backdrop-blur-md">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-sora text-xl font-bold text-navy" data-testid="drawer-lead-name">{lead.name || "Unnamed lead"}</h3>
                  <p className="text-sm text-ink/60">{lead.business_name || lead.email || lead.phone || "—"}</p>
                </div>
                <button onClick={() => onOpenChange(false)} className="rounded-lg p-1.5 text-ink/40 hover:bg-mist" data-testid="drawer-close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StageChip value={lead.stage} />
                <QualityChip value={lead.quality} />
                <SlaChip sla={lead.sla} full />
                {lead.is_unassigned && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">Unassigned</span>}
                {lead.possible_duplicate && <span data-testid="drawer-possible-dup" className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700"><Copy className="h-3 w-3" />Possible duplicate</span>}
                {isAdmin && (
                  <button onClick={() => setDelOpen(true)} data-testid="drawer-delete-button" className="ml-auto flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-0.5 text-xs font-semibold text-red-500 hover:bg-red-50">
                    <Trash2 className="h-3 w-3" />Delete
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-4 p-5">
              {/* Pinned Notes */}
              {pinnedNotes.length > 0 && (
                <div data-testid="pinned-notes" className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
                  <div className="mb-2.5 flex items-center gap-2">
                    <Pin className="h-4 w-4 text-amber-600" />
                    <h4 className="font-sora text-sm font-semibold text-navy">Pinned Notes</h4>
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">{pinnedNotes.length}</span>
                  </div>
                  <div className="space-y-2">
                    {(pinnedExpanded ? pinnedNotes : pinnedNotes.slice(0, 2)).map((n) => <NoteCard key={n.id} note={n} />)}
                  </div>
                  {pinnedNotes.length > 2 && (
                    <button onClick={() => setPinnedExpanded((v) => !v)} data-testid="pinned-view-all" className="mt-2 text-xs font-medium text-loop hover:underline">
                      {pinnedExpanded ? "Show less" : `View all ${pinnedNotes.length} pinned notes`}
                    </button>
                  )}
                </div>
              )}

              {/* Identity */}
              <Section title="Identity">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Phone">
                    {lead.phone ? (
                      <div className="flex items-center gap-2">
                        <a href={`tel:${phoneDigits}`} data-testid="lead-call-link" className="flex items-center gap-1 text-loop hover:underline"><Phone className="h-3.5 w-3.5" />{lead.phone}</a>
                        <a href={`https://wa.me/${phoneDigits.replace("+", "")}`} target="_blank" rel="noreferrer" data-testid="lead-whatsapp-link" className="text-emerald-600"><MessageCircle className="h-4 w-4" /></a>
                      </div>
                    ) : null}
                  </Field>
                  <Field label="Email">
                    {lead.email ? <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-loop hover:underline"><Mail className="h-3.5 w-3.5" />{lead.email}</a> : null}
                  </Field>
                  <Field label="City">{lead.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-ink/40" />{lead.city}</span>}</Field>
                  <Field label="Business">{lead.business_name && <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5 text-ink/40" />{lead.business_name}</span>}</Field>
                  <Field label="Website">{lead.website && <a href={lead.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-loop hover:underline"><Globe className="h-3.5 w-3.5" />Visit</a>}</Field>
                </div>
              </Section>

              {/* Quick Actions (stage / quality / owner only — qualification answers live in the read-only Snapshot below) */}
              <Section title="Quick Actions">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-sky/70 bg-mist/50 p-3">
                    <p className="mb-1.5 text-xs font-medium text-ink/60">Stage</p>
                    <div className="flex items-center justify-between gap-2">
                      <span data-testid="drawer-stage-chip"><StageChip value={lead.stage} /></span>
                      <button onClick={() => { setChangeStageOpen(true); logStageChangeOpened(leadId); }} data-testid="drawer-change-stage-button" className="flex items-center gap-1 rounded-lg bg-loop px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-loophover">Change<ArrowRight className="h-3 w-3" /></button>
                    </div>
                  </div>
                  <div className="rounded-xl border border-sky/70 bg-mist/50 p-3">
                    <p className="mb-1.5 text-xs font-medium text-ink/60">Quality {lead.quality_overridden && <span className="text-[10px] font-semibold text-amber-600">(override)</span>}</p>
                    <div className="flex items-center justify-between gap-2">
                      <span data-testid="drawer-quality-chip"><QualityChip value={lead.quality} /></span>
                      {can("reassign") && <button onClick={() => setOverrideOpen(true)} data-testid="drawer-override-quality-button" className="rounded-lg border border-sky px-2.5 py-1.5 text-[11px] font-semibold text-ink/70 hover:bg-mist">Override</button>}
                    </div>
                  </div>
                </div>
                {/* Owner */}
                <div className="mt-4 flex items-center justify-between rounded-xl bg-mist px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy text-xs font-bold text-white">{initials(lead.owner_name)}</span>
                    <div>
                      <p className="text-xs text-ink/50">Sales Owner</p>
                      <p className="text-sm font-semibold text-navy">{lead.owner_name || "Unassigned"}</p>
                    </div>
                  </div>
                  {has("leads.assign") && (
                    <div className="flex items-center gap-2">
                      <select data-testid="drawer-reassign-select" onChange={(e) => e.target.value && reassign(e.target.value)} defaultValue="" className="h-9 rounded-lg border border-sky bg-white px-2 text-xs outline-none focus:border-loop">
                        <option value="" disabled>Reassign…</option>
                        {users.filter((u) => u.role !== "Admin").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                      {has("leads.assign") && (
                        <button onClick={rerun} data-testid="drawer-rerun-button" title="Re-run assignment rule" className="rounded-lg border border-sky p-2 text-loop hover:bg-sky/40"><RotateCw className="h-4 w-4" /></button>
                      )}
                    </div>
                  )}
                </div>
              </Section>

              {/* Qualification Snapshot */}
              <Section title="Qualification Snapshot" action={
                qualSnapshot?.has_qualification ? (
                  <button onClick={() => setQualEditOpen(true)} data-testid="qual-request-edit" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-[11px] font-semibold text-ink/70 hover:bg-mist">
                    <PencilLine className="h-3 w-3" />Request Edit
                  </button>
                ) : null
              }>
                {!qualSnapshot?.has_qualification ? (
                  <p className="text-sm text-ink/50" data-testid="qual-snapshot-empty">No qualification recorded yet. Use “Change Stage” to qualify this lead.</p>
                ) : (
                  <div className="space-y-3" data-testid="qual-snapshot">
                    <div className="flex flex-wrap items-center gap-2">
                      <QualityChip value={qualSnapshot.quality} />
                      {qualSnapshot.status && qualSnapshot.status !== "Qualified" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{qualSnapshot.status}</span>}
                      {qualSnapshot.score !== null && qualSnapshot.score !== undefined && <span className="text-xs font-medium text-ink/60">Score {qualSnapshot.score}</span>}
                      {qualSnapshot.flow_name && <span className="text-xs text-ink/45">· {qualSnapshot.flow_name}</span>}
                      {qualSnapshot.legacy ? (
                        <span data-testid="qual-legacy-badge" className="rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500">Legacy format</span>
                      ) : qualSnapshot.flow_version ? (
                        <span data-testid="qual-version-badge" className="rounded-full border border-sky bg-sky/20 px-2 py-0.5 text-[10px] font-semibold text-loop">Form v{qualSnapshot.flow_version}</span>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      {(() => {
                        const answeredItems = (qualSnapshot.items || []).filter((it) => it.answered);
                        if (answeredItems.length === 0) return <p className="text-xs text-ink/45" data-testid="qual-items-empty">No answers were recorded for this qualification.</p>;
                        return answeredItems.map((it, i) => (
                          <div key={i} data-testid={`qual-item-${i}`} className="rounded-lg bg-mist px-3 py-2">
                            <p className="text-xs font-medium text-ink/50">{it.question_label}</p>
                            <p className="mt-0.5 text-sm text-ink">{it.answer}</p>
                          </div>
                        ));
                      })()}
                    </div>
                    <p className="text-[11px] text-ink/45">
                      Qualified by {qualSnapshot.completed_by || "—"} · {fmtDateTime(qualSnapshot.completed_at)}
                      {qualSnapshot.updated_by && <span> · last updated by {qualSnapshot.updated_by} · {fmtDateTime(qualSnapshot.updated_at)}</span>}
                    </p>
                  </div>
                )}
              </Section>

              {/* Deal Details (versioned, read-only) */}
              {(dealDetails?.has_deal || ["Proposal Sent", "Negotiation"].includes(lead.stage)) && (
                <Section title="Deal Details" action={
                  <button onClick={() => setDealModalOpen(true)} data-testid="deal-add-edit-btn" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50">
                    {dealDetails?.has_deal ? <><Pencil className="h-3 w-3" />Edit</> : <><Plus className="h-3 w-3" />Add Deal Details</>}
                  </button>
                }>
                  {!dealDetails?.has_deal ? (
                    <p className="text-sm text-ink/50" data-testid="deal-empty">No deal details yet. Add the commercial proposal for this lead.</p>
                  ) : (
                    <div className="space-y-3" data-testid="deal-snapshot">
                      <div className="flex flex-wrap items-center gap-2">
                        {dealDetails.deal.deal_summary?.total_amount != null && (
                          <span className="rounded-lg bg-emerald-50 px-2.5 py-1 text-sm font-bold text-emerald-700">{fmtINR(dealDetails.deal.deal_summary.total_amount)}</span>
                        )}
                        {dealDetails.deal.deal_summary?.probability && <span className="rounded-full bg-mist px-2 py-0.5 text-[11px] font-medium text-ink/70">{dealDetails.deal.deal_summary.probability}</span>}
                        {dealDetails.deal.form_version && <span data-testid="deal-version-badge" className="rounded-full border border-sky bg-sky/20 px-2 py-0.5 text-[10px] font-semibold text-loop">Form v{dealDetails.deal.form_version}</span>}
                      </div>
                      <div className="space-y-2">
                        {dealDetails.deal.items.filter((i) => i.answered).map((it) => (
                          <div key={it.field_id} data-testid={`deal-item-${it.field_id}`} className="rounded-lg bg-mist px-3 py-2">
                            <p className="text-xs font-medium text-ink/50">{it.label}</p>
                            <p className="mt-0.5 text-sm text-ink">{it.field_type === "currency" ? fmtINR(it.answer) : it.answer}</p>
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-ink/45">
                        Proposal by {dealDetails.deal.created_by_name || "—"} · {fmtDateTime(dealDetails.deal.created_at)}
                        {dealDetails.deal.updated_by_name && <span> · last updated by {dealDetails.deal.updated_by_name} · {fmtDateTime(dealDetails.deal.updated_at)}</span>}
                      </p>
                    </div>
                  )}
                </Section>
              )}

              {/* Won Deal & Payments */}
              {form.stage === "Won" && (
                <Section title="Deal & Payments" action={
                  <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700"><Trophy className="h-3.5 w-3.5" />Won</span>
                }>
                  <p className="mb-2 text-xs font-medium text-ink/60">Scope of work / services in final deal</p>
                  <div className="mb-4 flex flex-wrap gap-2">
                    {(options?.services || []).map((s) => {
                      const on = (form.deal_services || []).includes(s);
                      return (
                        <button key={s} type="button" data-testid={`deal-service-${s}`} onClick={() => toggleService(s)}
                          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-loop bg-loop text-white" : "border-sky bg-white text-ink/70 hover:bg-mist"}`}>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mb-4 grid grid-cols-2 gap-3">
                    <div><p className="mb-1 text-xs font-medium text-ink/60">Final Deal Value (₹)</p>
                      <input type="number" data-testid="deal-final-value" value={form.deal_value_estimate || ""} onChange={(e) => set("deal_value_estimate", e.target.value ? Number(e.target.value) : null)} className="h-10 w-full rounded-lg border border-sky px-2.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
                    </div>
                    <div><p className="mb-1 text-xs font-medium text-ink/60">Payment Structure</p>
                      <Select testid="deal-payment-structure" value={form.payment_structure} onChange={(v) => set("payment_structure", v)} options={options?.payment_structures || []} placeholder="Select structure" />
                    </div>
                  </div>
                  <button onClick={save} disabled={saving} data-testid="deal-save-button" className="mb-4 w-full rounded-lg bg-loop py-2 text-xs font-semibold text-white hover:bg-loophover disabled:opacity-60">Save deal details</button>

                  {/* Payments ledger */}
                  <div className="rounded-xl bg-mist p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-navy"><Banknote className="h-4 w-4 text-emerald-600" />Payments received</p>
                      <p className="font-sora text-sm font-bold text-emerald-700" data-testid="payments-total">{fmtINR((form.payments || []).reduce((s, p) => s + (p.amount || 0), 0))}</p>
                    </div>
                    {(form.payments || []).length > 0 ? (
                      <div className="mb-3 space-y-1.5">
                        {form.payments.map((p, i) => (
                          <div key={i} className="flex items-center justify-between rounded-lg bg-white px-3 py-1.5 text-sm">
                            <span className="font-medium text-navy">{fmtINR(p.amount)}</span>
                            <span className="text-xs text-ink/50">{p.note || "Payment"} · {fmtDate(p.received_at)}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="mb-3 text-xs text-ink/50">No payments recorded yet.</p>}
                    <div className="flex flex-wrap items-center gap-2">
                      <input type="number" data-testid="payment-amount-input" placeholder="Amount ₹" value={newPay.amount} onChange={(e) => setNewPay((n) => ({ ...n, amount: e.target.value }))} className="h-9 w-24 rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop" />
                      <input type="date" data-testid="payment-date-input" value={newPay.received_at} onChange={(e) => setNewPay((n) => ({ ...n, received_at: e.target.value }))} className="h-9 rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop" />
                      <input data-testid="payment-note-input" placeholder="Note" value={newPay.note} onChange={(e) => setNewPay((n) => ({ ...n, note: e.target.value }))} className="h-9 flex-1 rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop" />
                      <button onClick={addPayment} data-testid="add-payment-button" className="flex h-9 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700"><Plus className="h-3.5 w-3.5" />Add</button>
                    </div>
                  </div>
                </Section>
              )}

              {/* Attribution */}
              <Section title="Where this lead came from">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Platform">{lead.platform}</Field>
                  <Field label="Type">{lead.is_organic ? "Organic" : "Paid"}</Field>
                  <Field label="Campaign">{lead.campaign_name}</Field>
                  <Field label="Campaign ID">{lead.campaign_id}</Field>
                  <Field label="Ad Set">{lead.adset_name}</Field>
                  <Field label="Ad Set ID">{lead.adset_id}</Field>
                  <Field label="Ad">{lead.ad_name}</Field>
                  <Field label="Ad ID">{lead.ad_id}</Field>
                  <Field label="Form">{lead.form_name}</Field>
                  <Field label="Form ID">{lead.form_id}</Field>
                  <Field label="Created">{fmtDate(lead.created_at)}</Field>
                  <Field label="Conversation">
                    {lead.inbox_url ? <a href={lead.inbox_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-loop hover:underline"><ExternalLink className="h-3.5 w-3.5" />Open</a> : null}
                  </Field>
                </div>
              </Section>

              {/* Imported from CRM (Bitrix / CSV import) */}
              {lead.import_metadata && (
                <Section title="Imported from CRM">
                  <div data-testid="import-meta" className="grid grid-cols-2 gap-4">
                    <Field label="Previous CRM Stage">{lead.import_metadata.previous_crm_stage}</Field>
                    <Field label="Previous Owner">{lead.import_metadata.previous_crm_owner}</Field>
                    <Field label="CRM Lead ID">{lead.import_metadata.previous_crm_lead_id}</Field>
                    <Field label="Repeat Lead">{lead.import_metadata.repeat_lead ? "Yes" : null}</Field>
                    <Field label="Created By (CRM)">{lead.import_metadata.previous_crm_created_by}</Field>
                    <Field label="Modified By (CRM)">{lead.import_metadata.previous_crm_modified_by}</Field>
                    <Field label="Modified At (CRM)">{lead.import_metadata.previous_crm_modified_at}</Field>
                    <Field label="Stage Changed By">{lead.import_metadata.previous_crm_stage_changed_by}</Field>
                    <Field label="Stage Change Date">{lead.import_metadata.previous_crm_stage_change_date}</Field>
                    <Field label="Last Contact (CRM)">{lead.import_metadata.previous_crm_last_contact}</Field>
                    <Field label="Last Updated (CRM)">{lead.import_metadata.previous_crm_last_updated}</Field>
                    <Field label="Bitrix Form ID">{lead.import_metadata.bitrix_form_id}</Field>
                  </div>

                  {/* Original CRM columns (raw_row) — every column from the source export */}
                  {lead.import_metadata.raw_row && Object.keys(lead.import_metadata.raw_row).length > 0 && (
                    <div className="mt-4">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink/40">Original CRM Columns</p>
                      <div className="space-y-2" data-testid="import-raw-row">
                        {Object.entries(lead.import_metadata.raw_row).map(([k, v]) => (
                          <div key={k} className="rounded-lg bg-mist px-3 py-2">
                            <p className="text-xs font-medium text-ink/50">{k}</p>
                            <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-ink">{v == null || v === "" ? "—" : String(v)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="mt-4 text-[11px] text-ink/45">
                    Imported from {lead.import_metadata.import_source || "CRM export"}
                    {lead.import_metadata.uploaded_by ? ` by ${lead.import_metadata.uploaded_by}` : ""}
                    {lead.import_metadata.uploaded_at ? ` · ${fmtDateTime(lead.import_metadata.uploaded_at)}` : ""}
                  </p>
                </Section>
              )}

              {/* Dynamic Form Answers */}
              {lead.dynamic_form_answers?.length > 0 && (
                <Section title="Form Answers">
                  <div className="space-y-3">
                    {lead.dynamic_form_answers.map((d, i) => (
                      <div key={i} className="rounded-lg bg-mist px-3 py-2">
                        <p className="text-xs font-medium text-ink/50">{d.question_label}</p>
                        <p className="mt-0.5 text-sm text-ink">{d.answer}</p>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Internal Notes (multiple) */}
              <Section title="Internal Notes" action={
                <button onClick={() => setNoteModal("new")} data-testid="add-note-button" className="flex items-center gap-1 rounded-lg bg-loop px-3 py-1.5 text-xs font-semibold text-white hover:bg-loophover">
                  <Plus className="h-3.5 w-3.5" />Add Note
                </button>
              }>
                {notes.length === 0 ? (
                  <p className="text-sm text-ink/50" data-testid="notes-empty">No notes yet. Add a private note about this lead.</p>
                ) : (
                  <div className="space-y-2.5">
                    {notes.map((n) => <NoteCard key={n.id} note={n} />)}
                  </div>
                )}
              </Section>

              {/* Activities */}
              <Section title="Activities" action={
                <button onClick={() => setActModal("new")} data-testid="drawer-add-activity" className="flex items-center gap-1 rounded-lg bg-loop px-3 py-1.5 text-xs font-semibold text-white hover:bg-loophover">
                  <Plus className="h-3.5 w-3.5" />Add Activity
                </button>
              }>
                {activities.length === 0 ? (
                  <p className="text-sm text-ink/50">No activities yet. Add a call, follow-up, meeting or task.</p>
                ) : (
                  <div className="space-y-2.5">
                    {activities.map((a) => {
                      const open = a.status === "Pending" || a.status === "Snoozed";
                      const overdue = open && isOverdue(a.due_at);
                      return (
                        <div key={a.id} data-testid={`drawer-activity-${a.id}`} className={`rounded-xl border p-3 ${overdue ? "border-red-200 bg-red-50/40" : "border-sky/70 bg-white"}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <ActivityTypeChip value={a.activity_type} />
                                <PriorityChip value={a.priority} />
                                {!open && <ActivityStatusChip value={a.status} />}
                              </div>
                              <p className="mt-1 text-sm font-semibold text-navy">{a.activity_title}</p>
                              {a.activity_note && <p className="text-xs text-ink/60">{a.activity_note}</p>}
                              <p className={`mt-0.5 text-[11px] ${overdue ? "font-semibold text-red-500" : "text-ink/40"}`}>
                                {a.due_at ? fmtDateTime(a.due_at) : "No due date"}{a.assigned_to_name ? ` · ${a.assigned_to_name}` : ""}
                              </p>
                              {a.status === "Completed" && <p className="text-[11px] text-emerald-600">Completed {fmtDateTime(a.completed_at)}{a.completed_by_name ? ` by ${a.completed_by_name}` : ""}</p>}
                            </div>
                          </div>
                          {open && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              <button data-testid={`drawer-activity-complete-${a.id}`} onClick={() => actAction(a.id, "complete")} className="flex items-center gap-1 rounded-lg border border-sky px-2 py-1.5 text-[11px] font-medium text-emerald-600 hover:bg-emerald-50"><CheckCircle2 className="h-3 w-3" />Done</button>
                              <button onClick={() => actAction(a.id, "snooze", 1)} className="rounded-lg border border-sky px-2 py-1.5 text-[11px] font-medium text-ink/70 hover:bg-mist">+1d</button>
                              <button onClick={() => actAction(a.id, "snooze", 3)} className="rounded-lg border border-sky px-2 py-1.5 text-[11px] font-medium text-ink/70 hover:bg-mist">+3d</button>
                              <button onClick={() => setActModal(a)} className="rounded-lg border border-sky p-1.5 text-loop hover:bg-sky/30"><Pencil className="h-3 w-3" /></button>
                              <button onClick={() => actAction(a.id, "cancel")} className="rounded-lg border border-sky p-1.5 text-red-500 hover:bg-red-50"><XCircle className="h-3 w-3" /></button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {lead.last_contacted_at && <p className="mt-3 text-xs text-ink/50">Last contacted {fmtDateTime(lead.last_contacted_at)}</p>}
              </Section>

              {/* Unified Timeline */}
              <Section title="Timeline" action={
                <div className="flex items-center gap-1 rounded-lg border border-sky/70 bg-mist p-0.5" data-testid="timeline-toggle">
                  <button onClick={() => setTimelineMode("key")} data-testid="timeline-key" className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${timelineMode === "key" ? "bg-white text-navy shadow-sm" : "text-ink/50"}`}>Key events</button>
                  <button onClick={() => setTimelineMode("all")} data-testid="timeline-all" className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${timelineMode === "all" ? "bg-white text-navy shadow-sm" : "text-ink/50"}`}>All</button>
                </div>
              }>
                {visibleEvents.length === 0 ? (
                  <p className="text-sm text-ink/50" data-testid="timeline-empty">No timeline events yet.</p>
                ) : (
                  <div className="space-y-5" data-testid="lead-timeline">
                    {timelineGroups.map((group) => (
                      <div key={group.label}>
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink/40">{group.label}</p>
                        <ol className="relative space-y-3 border-l border-sky pl-5">
                          {group.items.map((e) => {
                            const meta = TL_META[e.category] || TL_META.system;
                            const Icon = meta.icon;
                            return (
                              <li key={e.id} className="relative" data-testid={`timeline-event-${e.category}`}>
                                <span className={`absolute -left-[31px] flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-white ${meta.tint}`}>
                                  <Icon className="h-3 w-3" />
                                </span>
                                <p className="text-sm font-medium text-navy">{e.label}</p>
                                {(e.old_value || e.new_value) && (e.category === "stage" || e.category === "qualification" || e.category === "assignment") && (
                                  <p className="text-xs text-ink/60">{e.old_value || "—"} <ArrowRight className="inline h-3 w-3" /> <span className="font-medium text-navy">{e.new_value || "—"}</span></p>
                                )}
                                {e.description && <p className="mt-0.5 text-xs text-ink/55">{e.description}</p>}
                                <p className="mt-0.5 text-[11px] text-ink/40">{e.user_name} · {fmtDateTime(e.at)}</p>
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </div>
            <ActivityModal
              open={!!actModal}
              onOpenChange={(v) => !v && setActModal(null)}
              leadId={leadId}
              leadName={lead.name}
              activity={actModal !== "new" ? actModal : null}
              users={users}
              onSaved={() => { loadActivities(); load(); onUpdated?.(); }}
            />
            <ChangeStageModal
              open={changeStageOpen}
              onOpenChange={setChangeStageOpen}
              leadId={leadId}
              lead={lead}
              onDone={() => { load(); loadActivities(); onUpdated?.(); }}
            />
            <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
              <DialogContent className="border-sky bg-white sm:max-w-md" data-testid="override-quality-modal">
                <DialogHeader><DialogTitle className="font-sora text-navy">Override Quality</DialogTitle></DialogHeader>
                <DialogDescription className="text-xs text-ink/60">Manually set lead quality. This bypasses the qualification score and is logged with your reason.</DialogDescription>
                <div>
                  <label className="mb-1 block text-sm font-medium text-ink/80">Quality</label>
                  <select data-testid="override-quality-select" value={overrideForm.quality} onChange={(e) => setOverrideForm((f) => ({ ...f, quality: e.target.value }))} className="h-10 w-full rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop">
                    <option value="">Select quality</option>
                    {(options?.qualities || []).map((q) => <option key={q} value={q}>{q}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-ink/80">Reason</label>
                  <textarea data-testid="override-quality-reason" value={overrideForm.reason} onChange={(e) => setOverrideForm((f) => ({ ...f, reason: e.target.value }))} rows={2} className="w-full rounded-lg border border-sky p-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
                </div>
                <button onClick={submitOverride} data-testid="override-quality-submit" className="h-10 w-full rounded-xl bg-loop text-sm font-semibold text-white hover:bg-loophover">Save override</button>
              </DialogContent>
            </Dialog>
            <ConfirmDialog
              open={delOpen}
              onOpenChange={setDelOpen}
              title="Delete this lead?"
              description="The lead will be moved to the recycle bin (soft-deleted) and removed from all lists and the pipeline. An Admin can restore it later from the recycle bin."
              confirmLabel="Delete lead"
              testid="lead-delete-confirm"
              onConfirm={doDelete}
            />
            <NoteModal
              open={!!noteModal}
              onOpenChange={(v) => !v && setNoteModal(null)}
              leadId={leadId}
              note={noteModal !== "new" ? noteModal : null}
              onSaved={() => { loadNotes(); load(); }}
            />
            <NoteHistoryModal
              open={!!noteHistoryNote}
              onOpenChange={(v) => !v && setNoteHistoryNote(null)}
              leadId={leadId}
              note={noteHistoryNote}
            />
            <QualEditRequestModal
              open={qualEditOpen}
              onOpenChange={setQualEditOpen}
              leadId={leadId}
              items={qualSnapshot?.items || []}
              onSent={() => load()}
            />
            <DealDetailsModal
              open={dealModalOpen}
              onOpenChange={setDealModalOpen}
              leadId={leadId}
              onSaved={() => { loadDealDetails(); load(); }}
            />
            <ConfirmDialog
              open={!!delNote}
              onOpenChange={(v) => !v && setDelNote(null)}
              title="Delete this note?"
              description="This note and its edit history will be permanently removed. This cannot be undone."
              confirmLabel="Delete note"
              testid="note-delete-confirm"
              onConfirm={doDeleteNote}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
